/**
 * t536u: "Report" for a message (every message menu: channels, DMs, threads, announcements, comments) or a person (the
 * person drawer and sheet), and "Block" for someone's direct messages. One dialog host, opened from anywhere with
 * openReport(); the same wording and rules as the iOS app (packages/shared/src/moderation.ts). The API checks again.
 */
import { useId, useState } from 'react';
import { Flag } from 'lucide-react';
import { create } from 'zustand';
import { useQuery } from '@tanstack/react-query';
import { qk } from '@tradeoutlet/data';
import { BLOCK_TEXT as B, REPORT_DETAILS_MAX, REPORT_REASONS, REPORT_TEXT as T, type ReportReason } from '@tradeoutlet/shared';
import { Button, Modal, Textarea, ask } from '@/components/ui';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { useAuth } from '@/lib/auth';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';

/** `about` names what it was about when it isn't a message (e.g. an announcement); it goes first in the details. */
export type ReportTarget = { type: 'message'; id: string } | { type: 'person'; id: string; name: string; about?: string };

export const useReportDialog = create<{ target: ReportTarget | null; open: (t: ReportTarget) => void; close: () => void }>(set => ({
  target: null,
  open: target => set({ target }),
  close: () => set({ target: null }),
}));
export const openReport = (t: ReportTarget) => useReportDialog.getState().open(t);

/** Rendered once in the app shell. */
export function ReportDialogHost() {
  const target = useReportDialog(s => s.target);
  const close = useReportDialog(s => s.close);
  return target ? <ReportDialog key={`${target.type}:${target.id}`} target={target} onClose={close} /> : null;
}

export function ReportDialog({ target, onClose }: { target: ReportTarget; onClose: () => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = useId();
  const send = async () => {
    if (!ws || !reason || busy) return;
    setBusy(true); setError(null);
    try {
      const more = details.trim();
      const r = await api.sendReport(ws, target.type === 'message'
        ? { target_type: 'message', message_id: target.id, reason, details: more || undefined }
        : { target_type: 'person', user_id: target.id, reason, details: (target.about ? [target.about, more].filter(Boolean).join('\n') : more).slice(0, REPORT_DETAILS_MAX) || undefined });
      toast(r.already ? T.already : T.sent, 'success');
      onClose();
    } catch (e) { setError(errorMessage(e) || T.failed); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={() => !busy && onClose()} title={target.type === 'message' ? T.titleMessage : T.titlePerson(target.name)}
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={busy} data-testid="report-cancel">{T.cancel}</Button>
        <Button onClick={() => void send()} disabled={!reason} loading={busy} data-testid="report-send">{T.send}</Button>
      </>}>
      <div className="space-y-3 text-sm" data-testid="report-dialog">
        <p className="text-slate-600 dark:text-slate-300">{target.type === 'message' ? T.leadMessage : T.leadPerson}</p>
        <fieldset>
          <legend className="mb-1 font-medium text-slate-800 dark:text-slate-100">{T.reason}</legend>
          <div className="space-y-1">
            {REPORT_REASONS.map(r => (
              <label key={r.key} className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-slate-200 px-3 has-[:checked]:border-slate-900 has-[:checked]:bg-slate-50 dark:border-slate-700 dark:has-[:checked]:border-white dark:has-[:checked]:bg-slate-800">
                <input type="radio" name={name} value={r.key} checked={reason === r.key} onChange={() => setReason(r.key)} data-testid={`report-reason-${r.key}`} />
                {r.label}
              </label>
            ))}
          </div>
        </fieldset>
        <label className="block">
          <span className="mb-1 block font-medium text-slate-800 dark:text-slate-100">{T.details}</span>
          <Textarea rows={3} maxLength={REPORT_DETAILS_MAX} value={details} onChange={e => setDetails(e.target.value)} placeholder={T.detailsPlaceholder} data-testid="report-details" />
        </label>
        <p className="text-xs text-slate-500">{T.private}</p>
        {error && <p role="alert" className="text-rose-700 dark:text-rose-300" data-testid="report-error">{error}</p>}
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ blocks
export const blocksKey = (ws: string | null) => ['blocks', ws] as const;
export function useBlocks() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: blocksKey(ws), queryFn: () => api.myBlocks(ws!), enabled: Boolean(ws), staleTime: 60_000 });
}

/** Block (after a confirm) or unblock someone; refreshes the chat list, which hides blocked people's DMs. */
export function useToggleBlock() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const blocks = useBlocks();
  const isBlocked = (id: string) => Boolean(blocks.data?.user_ids.includes(id));
  const toggle = async (id: string, name: string) => {
    if (!ws) return;
    const blocked = isBlocked(id);
    if (!blocked && !(await ask({ title: B.title(name), body: B.body, confirmLabel: B.confirm, cancelLabel: B.cancel }))) return;
    try {
      if (blocked) await api.unblock(ws, id); else await api.block(ws, id);
      toast(blocked ? B.unblocked(name) : B.blocked(name), 'success');
      void queryClient.invalidateQueries({ queryKey: blocksKey(ws) });
      void queryClient.invalidateQueries({ queryKey: qk.conversations(ws) });
    } catch (e) { toast(errorMessage(e), 'error'); }
  };
  return { isBlocked, toggle };
}

/** "Report" on an announcement (or another post that isn't a chat message): a report about its author, naming the post. */
export function ReportPostButton({ authorId, authorName, what, className, icon }: { authorId: string | null | undefined; authorName: string | null | undefined; what: string; className?: string; icon?: boolean }) {
  const { userId } = useAuth();
  if (!authorId || authorId === userId) return null;
  const open = () => openReport({ type: 'person', id: authorId, name: authorName || 'this person', about: what });
  return icon
    ? <button type="button" onClick={open} aria-label={T.menu} title={T.menu} className={className ?? 'flex h-11 w-11 items-center justify-center text-slate-500'} data-testid="report-post"><Flag className="h-5 w-5" /></button>
    : <button type="button" onClick={open} className={className ?? 'inline-flex items-center gap-1 text-sm text-slate-500 hover:underline'} data-testid="report-post"><Flag className="h-4 w-4" aria-hidden />{T.menu}</button>;
}
